import React, { useEffect } from 'react';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectRobotsData } from '../../../store/robots/robots-slice-selectors';
import {
  selectIsUserAdminOrHigher,
  selectUser,
  selectVersionsData,
} from '../../../store/general/general-slice-selectors';
import OrganizationRole from '../../../models/enums/organization-roles';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DeleteIcon, InfoIcon, VersionIcon } from '@common/utils/icons/icons';
import { getUpdateVersions, getVersionSets } from '../../../store/general/general-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import BuildVersion from '../../../models/enums/build-version';
import CustomLabelTextTable from '../../misc/CustomLabelTextTable';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { patchRobotById } from '../../../store/robots/robots-actions';
import SelectInput from '../../forms/SelectInput';
import { SelectChangeEvent } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import VersionSet from '../../../models/general/versions/version-set';
import UserRole from '../../../models/enums/user-roles';
import OsVariant from '../../../models/enums/os-variant';

const VersionSettings: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const user = useAppSelector((state) => selectUser(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const versionsData = useAppSelector((state) => selectVersionsData(state));

  // moderators are not allowed to fetch versions
  const isManufacturerAdmin =
    isAdmin &&
    user.data.role !== UserRole.MODERATOR &&
    user.data.organization.role === OrganizationRole.MANUFACTURER;
  const robot = robotsData.currentRobot;

  useEffect(() => {
    if (isManufacturerAdmin && versionsData.sets.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getVersionSets());
    }
    if (isManufacturerAdmin && versionsData.versions.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getUpdateVersions());
    }
  }, [
    dispatch,
    isManufacturerAdmin,
    versionsData.sets.rest.getStatus,
    versionsData.versions.rest.getStatus,
  ]);

  if (!isManufacturerAdmin || robot == null || robot.osVariant === OsVariant.TORIZON) return null;

  const getVersionById = (id?: string | null) => {
    return versionsData.versions.data?.find((v) => v.id === id)?.version ?? t('info-not-set');
  };

  const deleteVersionClickHandler = (id: string) => {
    if (robot != null) {
      dispatch(
        patchRobotById({
          id: robot.id,
          data: { versionIds: robot.versionIds.filter((vId) => vId !== id) },
        }),
      );
    }
  };

  const versionChangeHandler = (event: SelectChangeEvent<unknown>) => {
    const version = event.target.value as string;
    dispatch(
      patchRobotById({ id: robot.id, data: { versionIds: [...robot.versionIds, version] } }),
    );
  };

  const getInfoTooltip = (versionSet: VersionSet) => {
    return (
      <TooltipIconButton
        tooltipProps={{
          placement: 'left',
          slotProps: {
            tooltip: {
              sx: {
                backgroundColor: 'white',
                boxShadow: 2,
              },
            },
          },
        }}
        tooltip={
          <CustomLabelTextTable
            cellPadding={0.5}
            rows={[
              {
                label: t('table-header-robotics-software'),
                text: getVersionById(versionSet?.roboticsSoftwareId),
              },
              {
                label: t('table-header-debian-meta-package'),
                text: getVersionById(versionSet?.debianMetaPackageId),
              },
              {
                label: t('table-header-management-containers'),
                text: getVersionById(versionSet?.managementContainersId),
              },
            ]}
          />
        }
        icon={<InfoIcon color="disabled" />}
      />
    );
  };

  return (
    <React.Fragment>
      {isManufacturerAdmin && user.data.organization.role === OrganizationRole.MANUFACTURER && (
        <CustomSplitColumnCard
          title={t('title-versioning')}
          content={[
            {
              left: (
                <Stack spacing={2} pr={2}>
                  <Stack direction="row" alignItems="center" spacing={1} py={1}>
                    <VersionIcon />
                    <Typography>{t('info-available-versions')}</Typography>
                  </Stack>
                  <CustomAlert severity="info" header={t('info-available-versions-description')} />
                </Stack>
              ),
              right: (
                <Stack pt={1}>
                  <SelectInput
                    value={''}
                    sx={{ mb: 1 }}
                    size="small"
                    label={t('input-label-version-set-tag')}
                    onChange={versionChangeHandler}
                    data-testid="select-version"
                  >
                    {versionsData.sets.data
                      ?.filter((vs) => !robot.versionIds.includes(vs.id))
                      .map((vs) => (
                        <MenuItem key={vs.id} value={vs.id}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                          >
                            <Typography variant="inherit">
                              {Object.values(BuildVersion).some((bv) => bv === vs?.tag)
                                ? t('build-version-' + vs?.tag)
                                : vs?.tag}
                            </Typography>
                            {getInfoTooltip(vs)}
                          </Stack>
                        </MenuItem>
                      ))}
                  </SelectInput>

                  {robot.versionIds.map((vId) => {
                    const versionSet = versionsData.sets.data?.find((vs) => vs.id === vId);
                    return (
                      <Stack
                        key={vId}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography>
                          {Object.values(BuildVersion).some((bv) => bv === versionSet?.tag)
                            ? t('build-version-' + versionSet?.tag)
                            : versionSet?.tag}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {versionSet != null && getInfoTooltip(versionSet)}
                          <TooltipIconButton
                            tooltipProps={{ placement: 'right' }}
                            tooltip={t('info-tooltip-remove-version-option-from-robot')}
                            icon={<DeleteIcon color="error" />}
                            onClick={() => deleteVersionClickHandler(vId)}
                          />
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              ),
            },
          ]}
        />
      )}
    </React.Fragment>
  );
};

export default VersionSettings;
